/**
 * Wachtrij-items die een BESTAND laten klinken: native als de APK het kan, anders in de
 * WebView.
 *
 * Spec: specs/20261020-spraakmeldingen-elevenlabs/spec.md FR-0.2, FR-0.3, plan O-3
 * Taak: T044 (tests: native/__tests__/KeepAliveBridge.test.ts, T039)
 *
 * ── DE KEUZE ────────────────────────────────────────────────────────────────
 *
 *   1. APK ≥ 1.0.6 (`KeepAliveBridge.supportsNativeAudio()`) én een native sleutel én bytes
 *      → één keer naar `getCacheDir()/voices/<key>.mp3`, dan `MediaPlayer`. Geen
 *      autoplaybeperking van de WebView.
 *   2. Anders, of als de native weg faalt VÓÓR er iets klonk → `HTMLAudioElement`, met de
 *      `play()`-`undefined`-guard: Chromium 69–84 geeft daar soms geen Promise terug.
 *
 * Een item eindigt pas wanneer het GELUID eindigt (`ended` / `audioEnded`), niet wanneer het
 * begint: anders begint de volgende chime of de stem er middenin. `onAudible` meldt het begin
 * apart, want dat is wat `soundPlayed: true` betekent.
 *
 * ⚠ Geen `AudioContext` hier. Een nieuwe context per geluid liet de tablet op 2026-10-23
 * crashen tijdens het aftellen; Web Audio loopt uitsluitend via `getUnlockedAudioContext()`
 * uit `@caro/shared/sip`, en dit bestand heeft het niet nodig.
 */

import type { AudioItem, AudioItemKind } from './AudioQueue';
import { KeepAliveBridge } from '../native/KeepAliveBridge';
import type { NativeAudioUsage } from '../native/KeepAliveBridge';
import { timeoutSignal } from '../utils/timeoutSignal';

/**
 * Een sleutel die veilig een bestandsnaam kan worden.
 *
 * De sleutel komt (deels) van de server — `${key}-${checksum}`, in fase 1 ook `h:<render_key>`.
 * De native kant weigert zelf alles buiten `[A-Za-z0-9._-]`, maar hier al opschonen houdt een
 * `:` uit een geldige sleutel bruikbaar in plaats van geweigerd. `..` kan nooit overblijven.
 */
export function safeAudioKey(key: string): string {
  const cleaned = String(key || '')
    .replace(/[^A-Za-z0-9._-]/g, '_')
    .replace(/\.{2,}/g, '_')
    .replace(/^[.]+/, '_')
    .slice(0, 120);
  return cleaned || 'audio';
}

function nativeAvailable(): boolean {
  try {
    const bridge = KeepAliveBridge as unknown as { supportsNativeAudio?: () => boolean };
    return typeof bridge.supportsNativeAudio === 'function' && bridge.supportsNativeAudio();
  } catch {
    return false;
  }
}

export interface FileItemOptions {
  kind?: Extract<AudioItemKind, 'chime' | 'clip' | 'file'>;
  /** Wat de WebView speelt: een `blob:`-URL uit de cache of een ingebouwd pad. */
  src: string;
  /** Sleutel in de native cache. Zonder sleutel geen native pad. */
  nativeKey?: string;
  /** De bytes voor de native cache, pas opgevraagd als ze er nog niet staan. */
  getBlob?: () => Promise<Blob | null | undefined>;
  usage?: NativeAudioUsage;
  durationMs?: number;
  volume?: number;
  /** Het geluid klinkt nu (`native` of `webview`). Hoogstens één keer per afspeelbeurt. */
  onAudible?: (path: 'native' | 'webview') => void;
}

/** Eén bestand als wachtrij-item. Werpt niet bij het aanmaken. */
export function fileItem(opts: FileItemOptions): AudioItem {
  let element: HTMLAudioElement | null = null;
  let usedNative = false;
  let stopped = false;

  const audible = (path: 'native' | 'webview') => {
    if (!opts.onAudible) return;
    try {
      opts.onAudible(path);
    } catch {
      /* een luisteraar mag het afspelen niet stoppen */
    }
  };

  /** `true` als het native pad het geluid gespeeld heeft; `false` = val terug op de WebView. */
  const tryNative = async (): Promise<boolean> => {
    if (!opts.nativeKey || !nativeAvailable()) return false;
    const key = safeAudioKey(opts.nativeKey);
    let started = false;
    try {
      let cached = await KeepAliveBridge.hasAudio(key);
      if (!cached && opts.getBlob) {
        const blob = await opts.getBlob();
        if (blob && blob.size > 0) {
          await KeepAliveBridge.cacheAudioBlob(key, blob);
          cached = true;
        }
      }
      if (!cached || stopped) return false;
      usedNative = true;
      await KeepAliveBridge.playAudioFile(key, {
        usage: opts.usage || 'notification',
        volume: opts.volume,
        onStarted: () => {
          started = true;
          audible('native');
        },
      });
      return true;
    } catch (err) {
      // Klonk er al iets, dan is dit een échte afspeelfout: niet nog eens via de WebView
      // beginnen, anders hoort de bewoner het begin twee keer.
      if (started) throw err;
      usedNative = false;
      return false;
    }
  };

  const playWebView = () => new Promise<void>((resolve, reject) => {
    if (stopped) {
      resolve();
      return;
    }
    let el: HTMLAudioElement;
    try {
      el = new Audio(opts.src);
      el.volume = typeof opts.volume === 'number' ? opts.volume : 1.0;
    } catch (err) {
      reject(err);
      return;
    }
    element = el;
    // `onended`/`onerror` als eigenschap en niet via addEventListener: zo gedraagt elke
    // WebView van de vloot zich hetzelfde (en de dubbels in de tests ook).
    el.onended = () => resolve();
    el.onerror = () => reject(new Error(`media error ${opts.src}`));
    let result: Promise<void> | undefined;
    try {
      result = el.play() as Promise<void> | undefined;
    } catch (err) {
      reject(err);
      return;
    }
    // ⚠ De `play()`-`undefined`-guard. Geen Promise = geen weigering te zien; aannemen dat het
    // begon en wachten op `ended`/`error` (en anders op de time-out van de wachtrij).
    if (result && typeof result.then === 'function') {
      result.then(() => audible('webview'), reject);
    } else {
      audible('webview');
    }
  });

  return {
    kind: opts.kind || 'file',
    durationMs: opts.durationMs,
    async play() {
      stopped = false;
      usedNative = false;
      if (await tryNative()) return;
      await playWebView();
    },
    stop() {
      stopped = true;
      if (usedNative) void KeepAliveBridge.stopAudio();
      const el = element;
      element = null;
      if (el) {
        try {
          el.onended = null;
          el.onerror = null;
          el.pause();
          el.currentTime = 0;
        } catch {
          /* al gestopt */
        }
      }
    },
  };
}

/**
 * De bytes van een INGEBOUWD geluid (`public/sounds/*.mp3`), voor de native cache.
 *
 * ⚠ Alleen een antwoord dat zich als `audio/*` aandient telt: de app-host antwoordt op elk
 * onbekend pad met 200 + `index.html` (de vergiftigde cache van 2026-09-24). En
 * `timeoutSignal()`, nooit `AbortSignal.timeout` (Chromium 69–84).
 */
export async function bundledBlob(src: string, timeoutMs = 3000): Promise<Blob | null> {
  try {
    const res = await fetch(src, { signal: timeoutSignal(timeoutMs) });
    const type = res && res.headers ? res.headers.get('content-type') : null;
    if (!res || !res.ok || !type || type.toLowerCase().indexOf('audio/') !== 0) return null;
    const blob = await res.blob();
    return blob && blob.size > 0 ? blob : null;
  } catch {
    return null;
  }
}

/**
 * Android-TTS (of Web Speech in de browser) als wachtrij-item, voor een trede van de ladder
 * (spraakmeldingen fase 1, T091). Faalt als er niet gesproken werd — ook bij een ontbrekende
 * stem voor de taal (T053a: `reason: 'no_voice'`) — zodat de ladder naar de volgende trede gaat.
 */
export function speechItem(
  text: string,
  lang: string,
  onOutcome?: (outcome: { spoken: boolean; reason?: string }) => void,
): AudioItem {
  return {
    kind: 'tts',
    durationMs: Math.max(2000, Math.min(60_000, String(text || '').length * 80)),
    async play() {
      let outcome: { spoken: boolean; reason?: string };
      try {
        const r = await KeepAliveBridge.speak(text, lang);
        outcome = r && typeof r.spoken === 'boolean' ? r : { spoken: true };
      } catch {
        outcome = { spoken: false, reason: 'error' };
      }
      if (onOutcome) {
        try {
          onOutcome(outcome);
        } catch {
          /* een luisteraar mag de ladder niet breken */
        }
      }
      if (!outcome.spoken) throw new Error(outcome.reason || 'not_spoken');
    },
    stop() {
      void KeepAliveBridge.stopTts();
    },
  };
}
